"use client"

import { addHours, startOfDay, isSameHour } from "date-fns"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import type { CalendarEvent } from "./calendar"
import UserSvg from "@/assets/userSvg.svg"
import { CalendarEventActions } from "./calendar-event-actions"

interface DayViewProps {
    currentDate: Date
    events: CalendarEvent[]
    onViewEvent?: (event: CalendarEvent) => void
    onEditEvent?: (event: CalendarEvent) => void
    onDeleteEvent?: (event: CalendarEvent) => void
    refetch?: () => void
    access?: string
    hideEventActions?: boolean
}

export function DayView({ currentDate, events, onViewEvent, onEditEvent, onDeleteEvent, refetch, access, hideEventActions }: DayViewProps) {
    const hours = Array.from({ length: 24 }, (_, i) => i) // 0:00 to 24:00

    // Get events for a specific hour
    const getEventsForHour = (hour: number) => {
        const hourStart = addHours(startOfDay(currentDate), hour)

        return events.filter((event) => {
            const eventStart = new Date(event.start)
            const eventHour = eventStart.getHours()
            return isSameHour(eventStart, hourStart)
        })
    }

    return (
        <div className="absolute inset-0 overflow-y-auto">
            {hours.map((hour) => {
                const eventsForHour = getEventsForHour(hour)

                return (
                    <div key={hour} className="flex border-b min-h-[100px]">
                        <div className="sticky left-0 z-10 w-16 flex-shrink-0 border-r p-2 text-xs text-gray-500 bg-white">{hour}:00</div>
                        <div className="flex-1 p-1">
                            {eventsForHour.map((event) => {
                                // Map color to static Tailwind classes
                                const getColorClasses = (color?: string) => {
                                    const colorMap: Record<string, string> = {
                                        purple: "bg-purple-50 border-purple-200 hover:bg-purple-100",
                                        blue: "bg-blue-50 border-blue-200 hover:bg-blue-100",
                                        green: "bg-green-50 border-green-200 hover:bg-green-100",
                                        red: "bg-red-50 border-red-200 hover:bg-red-100",
                                        yellow: "bg-yellow-50 border-yellow-200 hover:bg-yellow-100",
                                        orange: "bg-orange-50 border-orange-200 hover:bg-orange-100",
                                        teal: "bg-teal-50 border-teal-200 hover:bg-teal-100",
                                        pink: "bg-pink-50 border-pink-200 hover:bg-pink-100",
                                        indigo: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100",
                                        gray: "bg-gray-50 border-gray-200 hover:bg-gray-100",
                                    };
                                    return colorMap[color || ""] || "bg-blue-50 border-blue-200 hover:bg-blue-100";
                                };

                                return (
                                    <div
                                        key={event.id}
                                        className={cn(
                                            "p-2 rounded-md mb-1 border transition-colors",
                                            getColorClasses(event.color),
                                        )}
                                    >
                                        <div className="flex items-start justify-between gap-1 mb-1">
                                            <div
                                                className="text-sm font-medium flex-1 cursor-pointer"
                                                onClick={() => onViewEvent?.(event)}
                                            >
                                                {event.title}
                                            </div>
                                            {!hideEventActions && (
                                                <CalendarEventActions
                                                    event={event}
                                                    onView={onViewEvent}
                                                    onEdit={onEditEvent}
                                                    onDelete={onDeleteEvent}
                                                    refetch={refetch}
                                                    access={access}
                                                    size="sm"
                                                    variant="dropdown"
                                                />
                                            )}
                                        </div>
                                        {
                                            event.tags && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {event.tags.map((tag, idx) => (
                                                        <span
                                                            key={idx}
                                                            className={cn("px-1.5 py-0.5 rounded-sm text-white text-[10px]", `bg-${tag.color}-500`)}
                                                        >
                                                            {tag.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            )
                                        }
                                        {
                                            event.user && (
                                                <div className="mt-1 flex justify-end">
                                                    <Avatar className="h-6 w-6" >
                                                        <AvatarImage src={UserSvg} />
                                                    </Avatar>
                                                </div>
                                            )
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )
            })}
        </div >
    )
}

